/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { connect } from 'react-redux'

import type { DeploymentGetResponse } from '@modules/cloud-api/v1/types'
import { withSmallErrorBoundary } from '@modules/cui/Boundary'
import withPermissions from '@modules/permissions-components/PermissionsGate/withPermissions'
import type { ElasticsearchCluster } from '@modules/ui-types'
import Feature from '@modules/utils/feature'

import { getDeploymentTemplateId } from '@/lib/stackDeployments/selectors/deploymentTemplates'
import { getVersion } from '@/lib/stackDeployments/selectors/fundamentals'
import { fetchDeploymentHealthRequest } from '@/reducers/asyncRequests/registry'
import { getCluster } from '@/reducers/clusters'
import { getDeploymentHealth } from '@/reducers/deploymentHealth'
import { getDeploymentTemplate } from '@/reducers/deploymentTemplates'
import { getEolStatus } from '@/reducers/eolStatus'
import { getOrganization } from '@/reducers/organizations'
import { getSaasUser } from '@/reducers/saasUsers'
import { getStackDeployment } from '@/reducers/stackDeployments'

import { fetchDeploymentTemplates } from '../../actions/topology/deploymentTemplates'
import { getConfigForKey, isFeatureActivated } from '../../selectors'
import { withStackDeploymentRouteParams } from '../StackDeploymentEditor'

import ChromeNavigation from './ChromeNavigation'

import type { ReduxState } from '@/types/redux'
import type { ConsumerProps, DispatchProps, RouteParams, StateProps } from './types'

const mapStateToProps = (
  state: ReduxState,
  { regionId: regionIdParam, deploymentId, stackDeploymentId, router: { params } }: ConsumerProps,
): StateProps => {
  const { userId, organizationId } = params
  const defaultRegionId = getConfigForKey(state, `DEFAULT_REGION`)
  const regionId = regionIdParam || defaultRegionId

  const manageRbacFeature = isFeatureActivated(state, Feature.manageRbac)
  const serverlessFeature = isFeatureActivated(state, Feature.serverless)
  const stackDeployment = getStackDeployment(state, stackDeploymentId)
  const deployment = deploymentId ? getCluster(state, regionId, deploymentId) : undefined

  const deploymentTemplate = resolveDeploymentTemplate({
    state,
    params,
    deployment,
    regionId,
    stackDeployment,
  })

  const isAnySaas = getConfigForKey(state, `APP_PLATFORM`) === `saas`

  return {
    regionId,
    deployment,
    deploymentTemplate,
    defaultRegionId,
    trafficFilteringFeature: isFeatureActivated(state, Feature.trafficFiltering),
    manageRbacFeature,
    serverlessFeature,
    lookupSaasUsers: isFeatureActivated(state, Feature.lookupSaasUsers),
    saasUserProfile: getSaasUser(state, userId!),
    organization: getOrganization(state, organizationId!),
    showUserApiKeys: isAnySaas,
    stackDeployment,
    eolStatus: getEolStatus(state),
    health: getDeploymentHealth(state, stackDeploymentId!),
    deploymentHealthRequest: fetchDeploymentHealthRequest(state, stackDeploymentId!),
    isAdminConsole: getConfigForKey(state, `CLOUD_UI_APP`) === `saas-adminconsole`,
    isOneConsoleEnabled: isFeatureActivated(state, Feature.oneConsole),
  }
}

const mapDispatchToProps: DispatchProps = {
  fetchDeploymentTemplates,
}

export default withSmallErrorBoundary(
  withStackDeploymentRouteParams(
    connect<StateProps, DispatchProps, ConsumerProps>(
      mapStateToProps,
      mapDispatchToProps,
    )(
      withPermissions({
        hasSearchDeploymentsPermission: {
          permissionsToCheck: [{ type: 'deployment', action: 'search' }],
        },
        hasSearchAllocatorsPermission: {
          permissionsToCheck: [{ type: 'allocator', action: 'search' }],
        },
        hasListApiKeysPermission: {
          permissionsToCheck: [{ type: 'api-key', action: 'list' }],
        },
        hasListUsersPermission: {
          permissionsToCheck: [{ type: 'admin-user', action: 'list' }],
        },
      })(ChromeNavigation),
    ),
  ),
)

function resolveDeploymentTemplate({
  state,
  params,
  deployment,
  regionId,
  stackDeployment,
}: {
  state: ReduxState
  params: Partial<RouteParams>
  deployment?: ElasticsearchCluster | null
  stackDeployment?: DeploymentGetResponse | null
  regionId?: string
}) {
  // navigation inside a deployment, so use the template ID referenced in the deployment
  if (stackDeployment) {
    const deploymentTemplateId = getDeploymentTemplateId({ deployment: stackDeployment })
    const version = getVersion({ deployment: stackDeployment })

    return getDeploymentTemplate(state, regionId!, deploymentTemplateId!, version)
  }

  if (deployment && deployment.plan) {
    return getDeploymentTemplate(
      state,
      regionId!,
      deployment.deploymentTemplateId!,
      deployment.plan.version,
    )
  }

  const { templateId } = params

  // navigation in deployment template CRUD area, so use the template ID from url params
  if (regionId && templateId) {
    return getDeploymentTemplate(state, regionId, templateId)
  }

  return
}
